import { apiV2Instance } from '../../lib/api-v2.client';

export type AvailableAction =
  | 'BUY_QUICKCAM_COIN'
  | 'JOIN_WAITLIST'
  | 'REJOIN_WAITLIST'
  | 'MANAGE_NOTIFICATIONS'
  | 'LEAVE_WAITLIST'
  | 'RETRY'
  | 'CHECK_AVAILABILITY';

export type AvailabilityUiState =
  | 'DIRECT_PURCHASE_AVAILABLE'
  | 'WAITLIST_AVAILABLE'
  | 'WAITLIST_ACTIVE'
  | 'WAITLIST_WITHDRAWN'
  | 'WAITLIST_UNAVAILABLE'
  | 'VERIFICATION_REQUIRED'
  | 'AVAILABILITY_UNKNOWN'
  | 'SERVICE_UNAVAILABLE';

export interface AvailabilityView {
  uiState: AvailabilityUiState;
  availableActions: AvailableAction[];
  lastUpdatedAt?: string;
}

export interface NotificationChannels {
  inApp: boolean;
  email: boolean;
  sms: boolean;
  push: boolean;
}

export interface GeoDebugInfo {
  isAllowed: boolean | null;
  country: string | null;
  clientIp: string | null;
}

function idempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `wl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function withIdempotency() {
  return {
    silent: true as const,
    headers: { 'Idempotency-Key': idempotencyKey() },
  };
}

export function unavailableView(): AvailabilityView {
  return {
    uiState: 'SERVICE_UNAVAILABLE',
    availableActions: ['RETRY', 'CHECK_AVAILABILITY'],
    lastUpdatedAt: new Date().toISOString(),
  };
}

function unwrapAvailabilityPayload(
  payload: any,
): Partial<AvailabilityView> | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  // Nest AppResponse: { success, data: { uiState, availableActions, ... } }
  if (
    payload.data &&
    typeof payload.data === 'object' &&
    payload.data.uiState
  ) {
    return payload.data;
  }
  if (payload.uiState) {
    return payload;
  }
  return payload.data;
}

function normalizeView(
  data: Partial<AvailabilityView> | undefined,
): AvailabilityView {
  if (!data?.uiState) {
    return unavailableView();
  }

  return {
    uiState: data.uiState,
    availableActions: data.availableActions || [],
    lastUpdatedAt: data.lastUpdatedAt || new Date().toISOString(),
  };
}

export class QuickcamCoinWaitlistService {
  static async getAvailability(): Promise<AvailabilityView> {
    try {
      const res = await apiV2Instance.get(
        '/api/members/me/quickcam-coin-availability',
        {
          silent: true,
        },
      );
      const unwrapped = unwrapAvailabilityPayload(res?.data);
      return normalizeView(unwrapped);
    } catch (err: any) {
      return unavailableView();
    }
  }

  static async getGeoDebug(): Promise<GeoDebugInfo> {
    try {
      const res = await apiV2Instance.get('/api/v2/user/check-geo-status', {
        silent: true,
      });
      const data = res?.data?.data ?? res?.data ?? {};
      return {
        isAllowed: typeof data?.isAllowed === 'boolean' ? data.isAllowed : null,
        country: data?.country ?? null,
        clientIp: data?.clientIp ?? null,
      };
    } catch (err: any) {
      return { isAllowed: null, country: null, clientIp: null };
    }
  }

  static async join(channels: NotificationChannels): Promise<void> {
    await apiV2Instance.post(
      '/api/members/me/quickcam-coin-waitlist',
      {
        productId: 'quickcam-coin',
        notificationPreferences: channels,
        termsVersion: 'v1',
        privacyNoticeVersion: 'v1',
      },
      withIdempotency(),
    );
  }

  static async rejoin(): Promise<void> {
    await apiV2Instance.post(
      '/api/members/me/quickcam-coin-waitlist/rejoin',
      {
        productId: 'quickcam-coin',
        notificationPreferences: {
          inApp: true,
          email: false,
          sms: false,
          push: false,
        },
        termsVersion: 'v1',
        privacyNoticeVersion: 'v1',
      },
      withIdempotency(),
    );
  }

  static async leave(): Promise<void> {
    await apiV2Instance.delete(
      '/api/members/me/quickcam-coin-waitlist',
      withIdempotency(),
    );
  }

  static async updateNotifications(
    channels: NotificationChannels,
  ): Promise<void> {
    await apiV2Instance.patch(
      '/api/members/me/quickcam-coin-waitlist/notifications',
      { notificationPreferences: channels },
      withIdempotency(),
    );
  }
}

export default QuickcamCoinWaitlistService;
